package link.erpango.quip.ui.transfer

import androidx.compose.foundation.layout.*
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material.CircularProgressIndicator
import androidx.compose.material.Icon
import androidx.compose.material.IconButton
import androidx.compose.material.Scaffold
import androidx.compose.material.Text
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.ArrowBack
import androidx.compose.material.icons.filled.CheckCircle
import androidx.compose.material.icons.filled.Warning
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.KeyboardCapitalization
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import androidx.navigation.NavController
import link.erpango.quip.features.transfer.TransferCode
import link.erpango.quip.features.transfer.TransferState
import link.erpango.quip.features.transfer.TransferViewModel
import link.erpango.quip.theme.QuipColors
import link.erpango.quip.ui.components.*

@Composable
fun AcceptTransferScreen(
    navController: NavController,
    token: String? = null,
    viewModel: TransferViewModel,
    onNavigateBack: () -> Unit = {}
) {
    var manualToken by remember { mutableStateOf(token ?: "") }
    val resolvedCode = TransferCode.resolveManualInput(manualToken)
    
    val transferState by viewModel.transferState.collectAsState()
    
    // If token is provided via QR scan, use it automatically
    LaunchedEffect(token) {
        if (token != null && token.isNotBlank()) {
            viewModel.acceptTransfer(token)
        }
    }
    
    Scaffold(
        backgroundColor = QuipColors.colorBackground
    ) { paddingValues ->
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(paddingValues)
                .padding(16.dp),
            verticalArrangement = Arrangement.spacedBy(16.dp),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            // Header with back button
            QuipTransparentHeader {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    IconButton(onClick = onNavigateBack) {
                        Icon(
                            Icons.Default.ArrowBack,
                            contentDescription = "Volver",
                            tint = QuipColors.colorText
                        )
                    }
                    Spacer(modifier = Modifier.width(8.dp))
                    Text(
                        text = if (transferState is TransferState.Success) "Transferencia Completada" else "Aceptar Transferencia",
                        style = androidx.compose.material.MaterialTheme.typography.h6,
                        fontWeight = androidx.compose.ui.text.font.FontWeight.Bold,
                        color = QuipColors.colorText
                    )
                }
            }
            
            Spacer(modifier = Modifier.height(16.dp))
            
            // Check if transfer was successful - show completion screen (Option B)
            if (transferState is TransferState.Success) {
                val successMsg = (transferState as TransferState.Success).message
                
                Icon(
                    Icons.Default.CheckCircle,
                    contentDescription = "Éxito",
                    tint = QuipColors.colorSuccess,
                    modifier = Modifier.size(80.dp)
                )
                
                Text(
                    text = "¡Transferencia completada!",
                    style = androidx.compose.material.MaterialTheme.typography.h5,
                    fontWeight = androidx.compose.ui.text.font.FontWeight.Bold,
                    color = QuipColors.colorSuccess,
                    modifier = Modifier.fillMaxWidth(),
                    textAlign = androidx.compose.ui.text.style.TextAlign.Center
                )
                
                Text(
                    text = successMsg,
                    style = androidx.compose.material.MaterialTheme.typography.body1,
                    color = QuipColors.colorTextSecondary,
                    modifier = Modifier.fillMaxWidth(),
                    textAlign = androidx.compose.ui.text.style.TextAlign.Center
                )
                
                Spacer(modifier = Modifier.weight(1f))
                
                // Manual back button - user decides when to go back (Option B)
                QuipPrimaryButton(
                    text = "Volver al inicio",
                    onClick = { 
                        navController.popBackStack("home", inclusive = false)
                    },
                    icon = Icons.Default.ArrowBack,
                    modifier = Modifier.fillMaxWidth()
                )
            } else {
                // Original form for accepting transfer
                
                Text(
                    text = "Aceptar transferencia de vehículo",
                    style = androidx.compose.material.MaterialTheme.typography.h5,
                    fontWeight = androidx.compose.ui.text.font.FontWeight.Bold,
                    color = QuipColors.colorText,
                    modifier = Modifier.fillMaxWidth(),
                    textAlign = androidx.compose.ui.text.style.TextAlign.Center
                )
                
                Text(
                    text = "Introduce el código de 8 caracteres que aparece bajo el QR del emisor",
                    style = androidx.compose.material.MaterialTheme.typography.body2,
                    color = QuipColors.colorTextSecondary,
                    modifier = Modifier.fillMaxWidth(),
                    textAlign = androidx.compose.ui.text.style.TextAlign.Center
                )
                
                Spacer(modifier = Modifier.height(8.dp))
                
                // Manual token input (for web fallback or manual entry)
                QuipTextField(
                    value = manualToken,
                    onValueChange = { manualToken = TransferCode.formatWhileTyping(it) },
                    label = "Código de transferencia",
                    placeholder = {
                        Text(
                            text = TransferCode.OTP_EXAMPLE,
                            color = QuipColors.colorTextSecondary,
                            fontFamily = androidx.compose.ui.text.font.FontFamily.Monospace
                        )
                    },
                    helperText = {
                        Text(
                            text = "Formato ${TransferCode.OTP_EXAMPLE}. No distingue mayúsculas, guiones ni espacios.",
                            color = QuipColors.colorTextSecondary
                        )
                    },
                    keyboardOptions = KeyboardOptions(
                        capitalization = KeyboardCapitalization.Characters,
                        autoCorrectEnabled = false,
                        keyboardType = KeyboardType.Ascii,
                        imeAction = ImeAction.Done
                    ),
                    modifier = Modifier.fillMaxWidth(),
                    isError = transferState is TransferState.Error && manualToken.isBlank()
                )
                
                // Accept button or loading spinner
                if (transferState is TransferState.Loading) {
                    CircularProgressIndicator(color = QuipColors.colorPrimary, modifier = Modifier.size(48.dp))
                    Text(
                        text = "Procesando transferencia...",
                        style = androidx.compose.material.MaterialTheme.typography.body2,
                        color = QuipColors.colorTextSecondary
                    )
                } else {
                    QuipPrimaryButton(
                        text = "Aceptar transferencia",
                        onClick = { resolvedCode?.let { viewModel.acceptTransfer(it) } },
                        enabled = resolvedCode != null && transferState !is TransferState.Loading,
                        icon = Icons.Default.CheckCircle
                    )
                    
                    // Show error if any
                    if (transferState is TransferState.Error) {
                        val errorMsg = (transferState as TransferState.Error).error
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Icon(
                                Icons.Default.Warning,
                                contentDescription = null,
                                tint = QuipColors.colorError,
                                modifier = Modifier.size(20.dp)
                            )
                            Spacer(modifier = Modifier.width(8.dp))
                            Text(
                                text = "Error: $errorMsg",
                                color = QuipColors.colorError,
                                style = androidx.compose.material.MaterialTheme.typography.body2,
                                modifier = Modifier.weight(1f)
                            )
                        }
                    }
                }
                
                Spacer(modifier = Modifier.weight(1f))
            }
        }
    }
}
